
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from './Button';
import ThemeToggle from './ThemeToggle';
import themeManager from '../../utils/themeManager';
import { scrollToTop } from '../../utils/smoothScroll';
import { IMAGE_VERSION } from '../../version.js';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(themeManager.getCurrentTheme());
  const [theme, setTheme] = useState(themeManager.getCurrentTheme());
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('header');

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = (event) => {
      setCurrentTheme(event.detail.theme);
      setTheme(event.detail.theme);
    };
    
    window.addEventListener('themeChanged', handleThemeChange);
    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, []);

  const navigationItems = [
    { label: t('nav.home'), path: '/', icon: 'Home', isHome: true },
    { label: t('nav.store'), path: '/xiaomi', icon: 'Store' },
    { label: t('nav.about'), path: '/about', icon: 'Info' },
    { label: t('nav.jobs'), path: '/job', icon: 'Briefcase' },
    { label: t('nav.b2b'), path: 'https://b2b.pccentar.rs/login.php', icon: 'Users', isExternal: true, isB2B: true }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path, isExternal = false) => {
    if (isExternal) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      // If already on the requested page, just scroll to top instead of re-navigating
      if (isActivePath(path)) {
        scrollToTop();
        setIsMobileMenuOpen(false);
        return;
      }

      let navPath = path;
      if (i18n.language === 'en') {
        navPath = '/en' + path;
      }
      navigate(navPath);
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActivePath = (path) => {
    const currentPath = location.pathname.replace(/^\/(en|sr)/, '') || '/';
    const targetPath = path.replace(/^\/(en|sr)/, '') || '/';
    
    // Special case for home: consider / and /homepage-hero-services as the same
    if (targetPath === '/' && (currentPath === '/' || currentPath === '/homepage-hero-services')) {
      return true;
    }
    
    return currentPath === targetPath;
  };

  // Map i18n language codes to display codes
  const getCurrentLanguage = () => {
    const lang = i18n.language || 'sr'; // Default to Serbian if language is not set
    if (lang.startsWith('sr')) return 'SR';
    if (lang.startsWith('en')) return 'EN';
    return 'SR';
  };
  
  const changeLanguage = (newLang) => {
    // 1. Uzmi trenutnu putanju (npr. /posao ili /en/posao)
    const currentPath = location.pathname;
    
    // 2. Očisti putanju od starih jezičkih prefiksa (/en ili /sr)
    const pathWithoutLang = currentPath.replace(/^\/(en|sr)/, '') || '/';
    
    // 3. Formiraj novu putanju
    // Ako je srpski, ide na koren (/posao), ako je engleski dodaje /en (/en/posao)
    let newPath = newLang === 'sr' ? pathWithoutLang : `/en${pathWithoutLang}`;
    
    // Očisti duple kose crte ako se pojave (npr //posao postane /posao)
    newPath = newPath.replace(/\/+/g, '/');

    // 4. Izvrši promenu
    i18n.changeLanguage(newLang);
    navigate(newPath);
    setIsMobileMenuOpen(false); // Zatvori mobilni meni ako je otvoren
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-navigation transition-all duration-300 ${
          isScrolled ? 'glassmorphism shadow-elevation' : 'bg-background/80 backdrop-blur-sm'
        }`}
      >
        <div className={`flex items-center justify-between ${isScrolled ? 'h-14' : 'h-header'} px-6 lg:px-8 transition-all duration-300`}>
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer spring-animation hover:scale-105"
            onClick={() => handleNavigation('/', false)}
          >
            <div className="relative select-none">
              <img
                src={
                  currentTheme === 'dark' || isScrolled
                    ? `/assets/images/logoo.png?ver=${IMAGE_VERSION}`
                    : `/assets/images/logo.png?ver=${IMAGE_VERSION}`
                }
                alt="PC Centar Logo"
                itemProp="logo"
                className={`${isScrolled ? 'w-32 h-32' : 'w-48 h-48'} object-contain pointer-events-none transition-all duration-300`}
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  KhtmlUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  userSelect: 'none'
                }}
              />
            </div>
          </div>

          {/* Desktop Navigation & Theme Toggle */}
          <div className="hidden lg:flex items-center space-x-8">
            <nav className="flex items-center space-x-8">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path, item?.isExternal)}
                className={`relative flex items-center space-x-2 px-4 py-2 rounded-lg font-body font-medium transition-all duration-300 spring-animation ${
                  item?.isB2B
                    ? 'text-white bg-[#9E1C2D] hover:bg-[#701d2a] shadow-lg border border-[#9E1C2D]/30'
                    : item?.isHome
                    ? `${isScrolled ? 'text-white/80 hover:text-white' : 'text-text-secondary hover:text-foreground'} hover:bg-muted/50 px-2 py-2`
                    : isActivePath(item?.path)
                    ? `${isScrolled ? 'text-white bg-white/10' : 'text-accent bg-accent/10'} shadow-elevation`
                    : `${isScrolled ? 'text-white/80 hover:text-white' : 'text-text-secondary hover:text-foreground'} hover:bg-muted/50`
                }`}
              >
                <Icon name={item?.icon} size={18} />
                {!item?.isHome && <span>{item?.label}</span>}
                {item?.isB2B && (
                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full ml-1">
                    Partner
                  </span>
                )}
                {!item?.isB2B && !item?.isHome && isActivePath(item?.path) && (
                  <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 h-0.5 rounded-full w-6 ${
                    isScrolled ? 'bg-white' : 'bg-accent'
                  }`} />
                )}
              </button>
            ))}
            </nav>
            
            {/* Language Selector */}
            <div className="flex items-center space-x-1 border-l border-border/20 pl-8">
              <button 
                onClick={() => changeLanguage('sr')}
                className={`px-3 py-1 rounded text-sm font-medium transition-all duration-300 ${
                  getCurrentLanguage() === 'SR'
                    ? 'text-white bg-accent shadow-md'
                    : `${isScrolled ? 'text-white/70 hover:text-white' : 'text-text-secondary hover:text-foreground'}`
                }`}
                title="Srpski"
              >
                SR
              </button>
              <button 
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1 rounded text-sm font-medium transition-all duration-300 ${
                  getCurrentLanguage() === 'EN'
                    ? 'text-white bg-accent shadow-md'
                    : `${isScrolled ? 'text-white/70 hover:text-white' : 'text-text-secondary hover:text-foreground'}`
                }`}
                title="English"
              >
                EN
              </button>
            </div>
            
            {/* Theme Toggle */}
            <ThemeToggle />
          </div>

          {/* Mobile Menu Button & Theme Toggle */}
          <div className="lg:hidden flex items-center space-x-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
            >
              <Icon 
                name={isMobileMenuOpen ? 'X' : 'Menu'} 
                size={24} 
                className={`${isScrolled ? 'text-white' : ''} transition-transform duration-300`}
              />
            </Button>
          </div>
        </div>
      </header>
      {/* Mobile Navigation Drawer */}
      <div 
        className={`fixed inset-0 z-drawer lg:hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={toggleMobileMenu}
        />
        
        {/* Drawer */}
        <div 
          className={`absolute top-0 right-0 h-full w-80 max-w-[85vw] glassmorphism shadow-depth transform transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center">
                <img 
                src="/assets/images/logoo.png?ver=${IMAGE_VERSION}"
                alt="PC Centar Company Logo" 
                className="h-10 w-auto object-contain pointer-events-none" 
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  KhtmlUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  userSelect: 'none'
                }}
              />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMobileMenu}
              >
                <Icon name="X" size={20} className="text-white" />
              </Button>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 p-6">
              <div className="space-y-2">
                {navigationItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path, item?.isExternal)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl font-body font-medium text-left transition-all duration-300 spring-animation ${
                      item?.isB2B
                        ? 'text-white bg-[#9E1C2D] hover:bg-[#701d2a] shadow-lg border border-[#9E1C2D]/30'
                        : isActivePath(item?.path)
                        ? 'text-accent bg-accent/10 shadow-elevation border border-accent/20'
                        : 'text-text-secondary hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="text-base">{item?.label}</span>
                    {item?.isB2B && (
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full ml-auto">
                        Partner
                      </span>
                    )}
                    {!item?.isB2B && isActivePath(item?.path) && (
                      <div className="ml-auto w-2 h-2 bg-accent rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </nav>

            {/* Footer */}
            <div className="p-6 border-t border-border">
              <div className="space-y-4">
                {/* Language Selector */}
                <div className="flex items-center justify-around">
                  <button 
                    onClick={() => changeLanguage('sr')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      getCurrentLanguage() === 'SR'
                        ? 'text-white bg-accent shadow-md'
                        : 'text-text-secondary hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    Srpski
                  </button>
                  <button 
                    onClick={() => changeLanguage('en')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      getCurrentLanguage() === 'EN'
                        ? 'text-white bg-accent shadow-md'
                        : 'text-text-secondary hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    English
                  </button>
                </div>

                {/* Company Info */}
                <div className="text-center border-t border-border pt-4">
                  <p className="font-caption text-sm text-text-secondary">
                    33+ Years of IT Excellence
                  </p>
                  <p className="font-caption text-xs text-text-secondary mt-1">
                    Professional IT Services & Solutions
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;

